import { humanizeToCorrectTitleCase } from 'shared/utils';

import {
  booleanFieldDefaults,
  dateOperators,
  dateRangeOperators,
  FieldTypes,
  globalOperators,
  ipFieldDefaults,
  listFieldDefaults,
  numericFieldDefaults,
  pastDateFieldDefaults,
  textFieldDefaults,
} from '../types';

const EXCLUDE = [
  'day',
  'month',
  'year',
  'hour',
  'minute',
  'timestamp',
  'friendlyName',
  'matchedItems',
];

const LONG_DATETIME_FIELDS = ['metadata_parseTime', 'metadata_receiptTime'];

const SORT = [
  'objectType',
  'uid',
  'sourceUid',
  'success',
  'severity',
  'metadata',
  // entities
  'device',
  'srcDevice',
  'dstDevice',
  'user',
  'targetUser',
  'file',
  // ports
  'srcPort',
  'dstPort',
  // network
  'http',
  'dns',
  'email',
  'bro',
  'ipProtocol',
  'tcpProtocol',
  'bytesIn',
  'bytesOut',
  'packetsIn',
  'packetsOut',
  // threat/vuln
  'threat',
  'vuln',
  // process
  'process',
  'processUid',
  'pid',
  'parentPid',
  'commandLine',
  // unknown/misc
  'action',
  'application',
  'baseImage',
  'changeType',
  'description',
  'flowState',
  'logonType',
  'moduleType',
  'resource',
  'normalized',
];

const OBJECT_TYPES = [
  'Audit',
  'AuditChange',
  'AuditResourceAccess',
  'Authentication',
  'AuthenticationPrivilegeEscalation',
  'Email',
  'Endpoint',
  'EndpointModuleLoad',
  'EndpointProcess',
  'FailedRecord',
  'Network',
  'NetworkDHCP',
  'NetworkDNS',
  'NetworkFlow',
  'NetworkHTTP',
  'NetworkProxy',
  'Notification',
  'NotificationVulnerability',
];

export const normalizedObjectTypes = OBJECT_TYPES.map((type) => ({
  label: humanizeToCorrectTitleCase(type),
  prop: type,
}));

export const getRecordFields = (schema: string): FieldTypes[] => {
  const parsedSchema = JSON.parse(schema);

  return (
    parsedSchema.fields
      .filter((f) => !EXCLUDE.includes(f.name))
      // Do not display fromUser related fields as they are deprecated
      .filter((f) => !f.name.startsWith('fromUser_'))
      .sort((a, b) => {
        const a0 = a.name.split('_')[0];
        const b0 = b.name.split('_')[0];
        const ai = SORT.includes(a0) ? SORT.indexOf(a0) : SORT.length;
        const bi = SORT.includes(b0) ? SORT.indexOf(b0) : SORT.length;

        return ai - bi;
      })
      // complexity 15
      // eslint-disable-next-line complexity
      .map((f) => {
        const nameParts = f.name.split('_');
        const name = nameParts.pop();

        const label = humanizeToCorrectTitleCase(f.name);
        let type = f.type;

        if (Array.isArray(type)) {
          if (type.length === 2 && type[0] === 'null') {
            type = type[1];
          } else {
            return null;
          }
        }

        let defaults: Partial<FieldTypes> | null = null;

        if (type === 'string') {
          if (name === 'ip' || name.endsWith('Ip')) {
            defaults = ipFieldDefaults;
          } else if (name === 'objectType') {
            defaults = {
              ...textFieldDefaults,
              operators: [...globalOperators],
              type: 'list',
              options: normalizedObjectTypes,
            };
          } else {
            defaults = textFieldDefaults;
          }
        } else if (type === 'long' && name.endsWith('Time')) {
          defaults = {
            ...pastDateFieldDefaults,
            operators: [
              ...globalOperators,
              ...dateRangeOperators.filter((d) => d.type === 'between'),
              ...dateOperators,
            ],
          };
        } else if (['int', 'long', 'float', 'double'].includes(type)) {
          defaults = numericFieldDefaults;
        } else if (type === 'boolean') {
          defaults = booleanFieldDefaults;
        } else {
          if (type.type === 'array' && type.items === 'string') {
            defaults = listFieldDefaults;
          }
        }

        if (!defaults) {
          return null;
        }

        return {
          ...defaults,
          label,
          prop: f.name,
          source: 'record',
          propSeparator: '_',
        };
      })
      .filter((f) => !!f)
  );
};

export const preProcessFilter = (filter: any) => {
  if (LONG_DATETIME_FIELDS.includes(filter.prop)) {
    filter.value = filter.value.value
      ? filter.value.value.getTime()
      : filter.value.getTime();
  }
};

export const postProcessFilter = (filter: any) => {
  if (LONG_DATETIME_FIELDS.includes(filter.prop)) {
    filter.value = {
      value: new Date(filter.value),
    };
  }
};
